import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  EyeIcon, 
  PaintBrushIcon,
  Cog6ToothIcon,
  RectangleStackIcon,
  CodeBracketIcon,
  PlusIcon,
  TrashIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { Button, Card, Spinner, Input, Badge } from '@realvisor/ui';
import { 
  useForm, 
  useUpdateForm, 
  usePipelines,
  type FormConfig, 
  type FormField, 
  type FormTheme 
} from '@/hooks';

// Default theme
const DEFAULT_THEME: FormTheme = {
  primaryColor: '#2563eb',
  backgroundColor: '#ffffff',
  textColor: '#111827',
  labelColor: '#374151',
  borderColor: '#d1d5db',
  errorColor: '#ef4444',
  successColor: '#10b981',
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 'base',
  borderRadius: 'lg',
  padding: 'normal',
  shadow: 'lg',
  showHeader: true,
  headerStyle: 'gradient',
  buttonStyle: 'solid',
  buttonFullWidth: true,
};

const DEFAULT_CONFIG: Partial<FormConfig> = {
  layout: 'single',
  showLabels: true,
  showPlaceholders: true,
  integrationType: 'standard',
  showSuccessMessage: true,
  trackingEnabled: true,
  theme: DEFAULT_THEME,
  fields: [],
};

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefon' },
  { value: 'number', label: 'Číslo' },
  { value: 'textarea', label: 'Dlouhý text' },
  { value: 'select', label: 'Výběr' },
  { value: 'checkbox', label: 'Zaškrtávací pole' },
  { value: 'date', label: 'Datum' },
  { value: 'address', label: 'Adresa' },
  { value: 'price', label: 'Cena' },
  { value: 'hidden', label: 'Skryté pole' },
];

const PRESET_FIELDS: FormField[] = [
  { name: 'firstName', label: 'Jméno', type: 'text', required: true, width: 'half' },
  { name: 'lastName', label: 'Příjmení', type: 'text', required: false, width: 'half' },
  { name: 'email', label: 'E-mail', type: 'email', required: true, width: 'full' },
  { name: 'phone', label: 'Telefon', type: 'phone', required: false, width: 'full' },
  { name: 'message', label: 'Zpráva', type: 'textarea', required: false, width: 'full' },
];

const PRESET_THEMES = [
  { name: 'Modrá (výchozí)', primary: '#2563eb', bg: '#ffffff' },
  { name: 'Zelená', primary: '#10b981', bg: '#ffffff' },
  { name: 'Fialová', primary: '#8b5cf6', bg: '#ffffff' },
  { name: 'Oranžová', primary: '#f59e0b', bg: '#ffffff' },
  { name: 'Červená', primary: '#ef4444', bg: '#ffffff' },
  { name: 'Tmavá', primary: '#3b82f6', bg: '#1f2937' },
];

type TabType = 'fields' | 'design' | 'settings' | 'integration';

// Speciální typy formulářů které nelze editovat v builderu
const SPECIAL_FORM_TYPES = ['valuo', 'hypoteka'];

export default function FormEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: form, isLoading } = useForm(id || '');
  const { data: pipelines } = usePipelines();
  const updateForm = useUpdateForm();

  const [activeTab, setActiveTab] = useState<TabType>('fields');
  const [config, setConfig] = useState<FormConfig | null>(null);
  const [formSettings, setFormSettings] = useState({
    name: '',
    isActive: true,
    targetPipelineId: '',
    defaultStageId: '',
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Přesměrovat speciální formuláře na theme editor
  useEffect(() => {
    if (form) {
      const formConfig = form.config as any;
      if (formConfig?.formType && SPECIAL_FORM_TYPES.includes(formConfig.formType)) {
        navigate(`/forms/${id}/theme`, { replace: true });
        return;
      }
    }
  }, [form, id, navigate]);

  useEffect(() => {
    if (form) {
      const mergedConfig = {
        ...DEFAULT_CONFIG,
        ...form.config,
        theme: { ...DEFAULT_THEME, ...form.config?.theme },
      } as FormConfig;
      setConfig(mergedConfig);
      setFormSettings({
        name: form.name,
        isActive: form.isActive,
        targetPipelineId: form.targetPipelineId || '',
        defaultStageId: form.defaultStageId || '',
      });
    }
  }, [form]);

  const handleSave = async () => {
    if (!id || !config) return;
    
    try {
      await updateForm.mutateAsync({
        id,
        data: {
          name: formSettings.name,
          isActive: formSettings.isActive,
          targetPipelineId: formSettings.targetPipelineId || undefined,
          defaultStageId: formSettings.defaultStageId || undefined,
          config,
        },
      });
      toast.success('Formulář uložen');
      setHasChanges(false);
    } catch (error) {
      toast.error('Nepodařilo se uložit formulář');
    }
  };

  const updateConfig = (updates: Partial<FormConfig>) => {
    if (!config) return;
    setConfig({ ...config, ...updates });
    setHasChanges(true);
  };

  const updateTheme = (updates: Partial<FormTheme>) => {
    if (!config) return;
    setConfig({ 
      ...config, 
      theme: { ...config.theme, ...updates } 
    });
    setHasChanges(true);
  };

  const addField = () => {
    if (!config) return;
    const newField: FormField = {
      name: `field_${Date.now()}`,
      label: 'Nové pole',
      type: 'text',
      required: false,
      width: 'full',
    };
    updateConfig({ fields: [...config.fields, newField] });
  };

  const addPresetField = (preset: FormField) => {
    if (!config) return;
    updateConfig({ fields: [...config.fields, { ...preset }] });
  };

  const updateField = (index: number, updates: Partial<FormField>) => {
    if (!config) return;
    const newFields = [...config.fields];
    const existingField = newFields[index];
    if (existingField) {
      newFields[index] = { ...existingField, ...updates } as FormField;
    }
    updateConfig({ fields: newFields });
  };

  const removeField = (index: number) => {
    if (!config) return;
    const newFields = config.fields.filter((_, i) => i !== index);
    updateConfig({ fields: newFields });
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    if (!config) return;
    const newFields = [...config.fields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newFields.length) return;
    const fieldA = newFields[index];
    const fieldB = newFields[newIndex];
    if (fieldA && fieldB) {
      newFields[index] = fieldB;
      newFields[newIndex] = fieldA;
    }
    updateConfig({ fields: newFields });
  };

  const selectedPipeline = pipelines?.find(p => p.id === formSettings.targetPipelineId);

  if (isLoading || !config) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!form) {
    return (
      <Card padding="lg">
        <div className="text-center py-8">
          <h3 className="text-sm font-medium text-gray-900">Formulář nenalezen</h3>
          <Link to="/forms" className="mt-2 text-sm text-primary-600">Zpět na seznam</Link>
        </div>
      </Card>
    );
  }

  const tabs = [
    { id: 'fields' as const, label: 'Pole', icon: RectangleStackIcon },
    { id: 'design' as const, label: 'Design', icon: PaintBrushIcon },
    { id: 'settings' as const, label: 'Nastavení', icon: Cog6ToothIcon },
    { id: 'integration' as const, label: 'Integrace', icon: CodeBracketIcon },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-4 mb-4">
        <div className="flex items-center gap-4">
          <Link to="/forms">
            <Button variant="outline" size="sm">
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <Input
                value={formSettings.name}
                onChange={(e) => {
                  setFormSettings({ ...formSettings, name: e.target.value });
                  setHasChanges(true);
                }}
                className="text-xl font-bold border-0 p-0 focus:ring-0"
              />
              <Badge variant={formSettings.isActive ? 'success' : 'default'}>
                {formSettings.isActive ? 'Aktivní' : 'Neaktivní'}
              </Badge>
              {hasChanges && (
                <Badge variant="warning">Neuložené změny</Badge>
              )}
            </div>
            <p className="text-sm text-gray-500">
              Kód: <code className="bg-gray-100 px-1 rounded">{form.code}</code>
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/forms/${id}/preview`}>
            <Button variant="outline" leftIcon={<EyeIcon className="h-4 w-4" />}>
              Náhled
            </Button>
          </Link>
          <Button onClick={handleSave} disabled={updateForm.isPending || !hasChanges}>
            {updateForm.isPending ? 'Ukládám...' : 'Uložit změny'}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex gap-6 overflow-hidden">
        {/* Tabs */}
        <div className="w-48 flex-shrink-0">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'fields' && (
            <FieldsTab 
              fields={config.fields}
              onAddField={addField}
              onAddPresetField={addPresetField}
              onUpdateField={updateField}
              onRemoveField={removeField}
              onMoveField={moveField}
            />
          )}
          {activeTab === 'design' && (
            <DesignTab 
              config={config}
              onUpdateConfig={updateConfig}
              onUpdateTheme={updateTheme}
            />
          )}
          {activeTab === 'settings' && (
            <SettingsTab 
              config={config}
              formSettings={formSettings}
              pipelines={pipelines || []}
              selectedPipeline={selectedPipeline}
              onUpdateConfig={updateConfig}
              onUpdateSettings={(updates) => {
                setFormSettings({ ...formSettings, ...updates });
                setHasChanges(true);
              }}
            />
          )}
          {activeTab === 'integration' && (
            <IntegrationTab 
              config={config}
              onUpdateConfig={updateConfig}
            />
          )}
        </div>

        {/* Live Preview */}
        <div className="w-96 flex-shrink-0 border-l border-gray-200 pl-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Náhled</h3>
          <div className="sticky top-0">
            <FormPreviewMini config={config} formName={formSettings.name} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Fields Tab Component
function FieldsTab({ 
  fields, 
  onAddField, 
  onAddPresetField,
  onUpdateField, 
  onRemoveField,
  onMoveField,
}: {
  fields: FormField[];
  onAddField: () => void;
  onAddPresetField: (preset: FormField) => void;
  onUpdateField: (index: number, updates: Partial<FormField>) => void;
  onRemoveField: (index: number) => void;
  onMoveField: (index: number, direction: 'up' | 'down') => void;
}) {
  const [expandedField, setExpandedField] = useState<number | null>(null);

  const existingFieldNames = fields.map(f => f.name);
  const availablePresets = PRESET_FIELDS.filter(p => !existingFieldNames.includes(p.name));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Pole formuláře</h2>
        <Button size="sm" leftIcon={<PlusIcon className="h-4 w-4" />} onClick={onAddField}>
          Přidat pole
        </Button>
      </div>

      {availablePresets.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-700 mb-2">Rychle přidat standardní pole:</p>
          <div className="flex flex-wrap gap-2">
            {availablePresets.map((preset) => (
              <button
                key={preset.name}
                onClick={() => onAddPresetField(preset)}
                className="px-3 py-1 text-sm bg-white border border-blue-300 rounded-full hover:bg-blue-100 text-blue-700"
              >
                + {preset.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {(fields || []).map((field, index) => (
          <div 
            key={field.name} 
            className="border border-gray-200 rounded-lg bg-white"
          >
            <div 
              className="flex items-center gap-3 p-3 cursor-pointer"
              onClick={() => setExpandedField(expandedField === index ? null : index)}
            >
              <div className="flex flex-col gap-1">
                <button 
                  onClick={(e) => { e.stopPropagation(); onMoveField(index, 'up'); }}
                  disabled={index === 0}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                >
                  <ArrowsUpDownIcon className="h-3 w-3 rotate-180" />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); onMoveField(index, 'down'); }}
                  disabled={index === fields.length - 1}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                >
                  <ArrowsUpDownIcon className="h-3 w-3" />
                </button>
              </div>
              <div className="flex-1">
                <span className="font-medium text-gray-900">{field.label}</span>
                <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  {FIELD_TYPES.find(t => t.value === field.type)?.label || field.type}
                </span>
                {field.required && <span className="ml-1 text-red-500">*</span>}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onRemoveField(index); }}
                className="text-gray-400 hover:text-red-500"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>

            {expandedField === index && (
              <div className="border-t border-gray-100 p-4 space-y-4 bg-gray-50">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Popisek</label>
                    <Input
                      value={field.label}
                      onChange={(e) => onUpdateField(index, { label: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Název pole</label>
                    <Input
                      value={field.name}
                      onChange={(e) => onUpdateField(index, { name: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Typ</label>
                    <select
                      value={field.type}
                      onChange={(e) => onUpdateField(index, { type: e.target.value as FormField['type'] })}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                      {FIELD_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Šířka</label>
                    <select
                      value={field.width || 'full'}
                      onChange={(e) => onUpdateField(index, { width: e.target.value as FormField['width'] })}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    >
                      <option value="full">Celá šířka</option>
                      <option value="half">Polovina</option>
                      <option value="third">Třetina</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Placeholder</label>
                  <Input
                    value={field.placeholder || ''}
                    onChange={(e) => onUpdateField(index, { placeholder: e.target.value })}
                    placeholder="Zadejte placeholder..."
                  />
                </div>

                {field.type === 'select' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Možnosti (jedna na řádek)</label>
                    <textarea
                      value={field.options?.map(o => `${o.value}|${o.label}`).join('\n') || ''}
                      onChange={(e) => {
                        const options = e.target.value.split('\n').filter(Boolean).map(line => {
                          const [value, label] = line.split('|');
                          return { value: value?.trim() || '', label: label?.trim() || value?.trim() || '' };
                        });
                        onUpdateField(index, { options });
                      }}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      rows={4}
                      placeholder="hodnota|Popisek&#10;byt|Byt&#10;dum|Rodinný dům"
                    />
                  </div>
                )}

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={field.required || false}
                      onChange={(e) => onUpdateField(index, { required: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Povinné pole</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {fields.length === 0 && (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
          <p className="text-gray-500 mb-2">Zatím nemáte žádná pole</p>
          <Button size="sm" onClick={onAddField}>Přidat první pole</Button>
        </div>
      )}
    </div>
  );
}

// Design Tab Component
function DesignTab({ 
  config, 
  onUpdateConfig, 
  onUpdateTheme 
}: {
  config: FormConfig;
  onUpdateConfig: (updates: Partial<FormConfig>) => void;
  onUpdateTheme: (updates: Partial<FormTheme>) => void;
}) {
  const theme = config.theme;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">Design formuláře</h2>

      {/* Preset Themes */}
      <Card padding="md">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Barevné schéma</h3>
        <div className="grid grid-cols-3 gap-2">
          {PRESET_THEMES.map((preset) => (
            <button
              key={preset.name}
              onClick={() => onUpdateTheme({ 
                primaryColor: preset.primary, 
                backgroundColor: preset.bg,
                textColor: preset.bg === '#1f2937' ? '#f9fafb' : '#111827',
                labelColor: preset.bg === '#1f2937' ? '#d1d5db' : '#374151',
              })}
              className={`p-3 rounded-lg border-2 transition-all ${
                theme.primaryColor === preset.primary && theme.backgroundColor === preset.bg
                  ? 'border-primary-500 ring-2 ring-primary-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div 
                  className="w-4 h-4 rounded-full" 
                  style={{ backgroundColor: preset.primary }}
                />
                <div 
                  className="w-4 h-4 rounded-full border border-gray-300" 
                  style={{ backgroundColor: preset.bg }}
                />
              </div>
              <span className="text-xs text-gray-600">{preset.name}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* Custom Colors */}
      <Card padding="md">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Vlastní barvy</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Hlavní barva</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={theme.primaryColor}
                onChange={(e) => onUpdateTheme({ primaryColor: e.target.value })}
                className="w-10 h-10 rounded cursor-pointer"
              />
              <Input
                value={theme.primaryColor}
                onChange={(e) => onUpdateTheme({ primaryColor: e.target.value })}
                className="flex-1"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Pozadí</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={theme.backgroundColor}
                onChange={(e) => onUpdateTheme({ backgroundColor: e.target.value })}
                className="w-10 h-10 rounded cursor-pointer"
              />
              <Input
                value={theme.backgroundColor}
                onChange={(e) => onUpdateTheme({ backgroundColor: e.target.value })}
                className="flex-1"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Layout */}
      <Card padding="md">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Rozložení</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Zaoblení rohů</label>
            <select
              value={theme.borderRadius}
              onChange={(e) => onUpdateTheme({ borderRadius: e.target.value as FormTheme['borderRadius'] })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="none">Žádné</option>
              <option value="sm">Malé</option>
              <option value="md">Střední</option>
              <option value="lg">Velké</option>
              <option value="xl">Extra velké</option>
              <option value="full">Plné</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Stín</label>
            <select
              value={theme.shadow}
              onChange={(e) => onUpdateTheme({ shadow: e.target.value as FormTheme['shadow'] })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="none">Žádný</option>
              <option value="sm">Malý</option>
              <option value="md">Střední</option>
              <option value="lg">Velký</option>
              <option value="xl">Extra velký</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Odsazení</label>
            <select
              value={theme.padding}
              onChange={(e) => onUpdateTheme({ padding: e.target.value as FormTheme['padding'] })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="compact">Kompaktní</option>
              <option value="normal">Normální</option>
              <option value="spacious">Prostorné</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Header & Button */}
      <Card padding="md">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Hlavička a tlačítko</h3>
        <div className="space-y-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={theme.showHeader}
              onChange={(e) => onUpdateTheme({ showHeader: e.target.checked })}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Zobrazit hlavičku</span>
          </label>
          
          {theme.showHeader && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Styl hlavičky</label>
              <select
                value={theme.headerStyle}
                onChange={(e) => onUpdateTheme({ headerStyle: e.target.value as FormTheme['headerStyle'] })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="simple">Jednoduchý</option>
                <option value="gradient">Gradient</option>
                <option value="image">S obrázkem</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-500 mb-1">Styl tlačítka</label>
            <select
              value={theme.buttonStyle}
              onChange={(e) => onUpdateTheme({ buttonStyle: e.target.value as FormTheme['buttonStyle'] })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="solid">Plné</option>
              <option value="outline">Obrys</option>
              <option value="gradient">Gradient</option>
            </select>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={theme.buttonFullWidth}
              onChange={(e) => onUpdateTheme({ buttonFullWidth: e.target.checked })}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Tlačítko na celou šířku</span>
          </label>
        </div>
      </Card>

      {/* Text */}
      <Card padding="md">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Texty</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Text tlačítka</label>
            <Input
              value={config.submitButtonText || 'Odeslat'}
              onChange={(e) => onUpdateConfig({ submitButtonText: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Zpráva po odeslání</label>
            <Input
              value={config.successMessage || 'Děkujeme!'}
              onChange={(e) => onUpdateConfig({ successMessage: e.target.value })}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}

// Settings Tab Component
function SettingsTab({ 
  config, 
  formSettings,
  pipelines,
  selectedPipeline,
  onUpdateConfig, 
  onUpdateSettings,
}: {
  config: FormConfig;
  formSettings: { name: string; isActive: boolean; targetPipelineId: string; defaultStageId: string };
  pipelines: any[];
  selectedPipeline: any;
  onUpdateConfig: (updates: Partial<FormConfig>) => void;
  onUpdateSettings: (updates: Partial<typeof formSettings>) => void;
}) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">Nastavení</h2>

      <Card padding="md">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Stav formuláře</h3>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formSettings.isActive}
            onChange={(e) => onUpdateSettings({ isActive: e.target.checked })}
            className="rounded border-gray-300 text-primary-600"
          />
          <span className="text-sm text-gray-700">Formulář je aktivní</span>
        </label>
      </Card>

      <Card padding="md">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Pipeline</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Cílová pipeline</label>
            <select
              value={formSettings.targetPipelineId}
              onChange={(e) => onUpdateSettings({ targetPipelineId: e.target.value, defaultStageId: '' })}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Výchozí pipeline</option>
              {pipelines.map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>{pipeline.name}</option>
              ))}
            </select>
          </div>
          {selectedPipeline && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Výchozí fáze</label>
              <select
                value={formSettings.defaultStageId}
                onChange={(e) => onUpdateSettings({ defaultStageId: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">První fáze</option>
                {selectedPipeline.stages?.map((stage: any) => (
                  <option key={stage.id} value={stage.id}>{stage.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </Card>

      <Card padding="md">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Chování</h3>
        <div className="space-y-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.showSuccessMessage}
              onChange={(e) => onUpdateConfig({ showSuccessMessage: e.target.checked })}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Zobrazit zprávu po odeslání</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.trackingEnabled}
              onChange={(e) => onUpdateConfig({ trackingEnabled: e.target.checked })}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Povolit tracking (GTM, Analytics)</span>
          </label>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Přesměrování po odeslání (URL)</label>
            <Input
              value={config.redirectUrl || ''}
              onChange={(e) => onUpdateConfig({ redirectUrl: e.target.value })}
              placeholder="https://example.com/dekujeme"
            />
          </div>
        </div>
      </Card>
    </div>
  );
}

// Integration Tab Component
function IntegrationTab({ 
  config, 
  onUpdateConfig 
}: {
  config: FormConfig;
  onUpdateConfig: (updates: Partial<FormConfig>) => void;
}) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">Integrace</h2>

      <Card padding="md">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Typ integrace</h3>
        <div className="space-y-2">
          {[
            { value: 'standard', label: 'Standardní', desc: 'Ukládá data do RealVisor CRM' },
            { value: 'valuo', label: 'Valuo API', desc: 'Ocenění nemovitosti přes Valuo' },
            { value: 'webhook', label: 'Webhook', desc: 'Odešle data na vlastní URL' },
            { value: 'custom', label: 'Custom', desc: 'Vlastní JavaScript kód' },
          ].map((option) => (
            <label 
              key={option.value}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                config.integrationType === option.value 
                  ? 'border-primary-500 bg-primary-50' 
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name="integrationType"
                value={option.value}
                checked={config.integrationType === option.value}
                onChange={(e) => onUpdateConfig({ integrationType: e.target.value as FormConfig['integrationType'] })}
                className="mt-1"
              />
              <div>
                <span className="font-medium text-gray-900">{option.label}</span>
                <p className="text-xs text-gray-500">{option.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </Card>

      {config.integrationType === 'valuo' && (
        <Card padding="md">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Valuo API nastavení</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">API klíč</label>
              <Input
                type="password"
                value={config.integrationConfig?.valuoApiKey || ''}
                onChange={(e) => onUpdateConfig({ 
                  integrationConfig: { ...config.integrationConfig, valuoApiKey: e.target.value }
                })}
                placeholder="Váš Valuo API klíč"
              />
            </div>
            <p className="text-xs text-gray-500">
              Formulář automaticky odešle data do Valuo API a výsledek ocenění uloží k leadu.
            </p>
          </div>
        </Card>
      )}

      {config.integrationType === 'webhook' && (
        <Card padding="md">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Webhook nastavení</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Webhook URL</label>
              <Input
                value={config.integrationConfig?.webhookUrl || ''}
                onChange={(e) => onUpdateConfig({ 
                  integrationConfig: { ...config.integrationConfig, webhookUrl: e.target.value }
                })}
                placeholder="https://your-api.com/webhook"
              />
            </div>
            <p className="text-xs text-gray-500">
              Data formuláře budou odeslána jako JSON POST request na tuto URL.
            </p>
          </div>
        </Card>
      )}

      {config.integrationType === 'custom' && (
        <Card padding="md">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Custom kód</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">JavaScript (volá se po odeslání)</label>
              <textarea
                value={config.integrationConfig?.customScript || ''}
                onChange={(e) => onUpdateConfig({ 
                  integrationConfig: { ...config.integrationConfig, customScript: e.target.value }
                })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
                rows={8}
                placeholder={`// formData obsahuje data z formuláře
// callback(success, message) zavolej po dokončení

async function onSubmit(formData, callback) {
  try {
    const response = await fetch('https://api.example.com', {
      method: 'POST',
      body: JSON.stringify(formData)
    });
    callback(true, 'Úspěch!');
  } catch (error) {
    callback(false, error.message);
  }
}`}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Custom CSS</label>
              <textarea
                value={config.integrationConfig?.customCss || ''}
                onChange={(e) => onUpdateConfig({ 
                  integrationConfig: { ...config.integrationConfig, customCss: e.target.value }
                })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
                rows={4}
                placeholder={`.rv-form { /* vlastní styly */ }`}
              />
            </div>
          </div>
        </Card>
      )}

      <Card padding="md" className="bg-amber-50 border-amber-200">
        <h3 className="text-sm font-medium text-amber-800 mb-2">💡 Tip pro složité formuláře</h3>
        <p className="text-xs text-amber-700">
          Pro formuláře s komplexní logikou (např. kalkulačky, vícekrokové formuláře, 
          integrace s externími API jako Valuo) doporučujeme použít <strong>Custom</strong> integraci 
          nebo nás kontaktovat pro individuální řešení.
        </p>
      </Card>
    </div>
  );
}

// Mini Preview Component
function FormPreviewMini({ config, formName }: { config: FormConfig; formName: string }) {
  const theme = config.theme;
  
  const borderRadiusMap = {
    none: '0',
    sm: '0.125rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    full: '9999px',
  };

  const shadowMap = {
    none: 'none',
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
  };

  const paddingMap = {
    compact: '1rem',
    normal: '1.5rem',
    spacious: '2rem',
  };

  return (
    <div 
      className="overflow-hidden transition-all"
      style={{ 
        backgroundColor: theme.backgroundColor,
        borderRadius: borderRadiusMap[theme.borderRadius],
        boxShadow: shadowMap[theme.shadow],
      }}
    >
      {theme.showHeader && (
        <div 
          className="px-4 py-3"
          style={{ 
            background: theme.headerStyle === 'gradient' 
              ? `linear-gradient(135deg, ${theme.primaryColor}, ${theme.primaryColor}dd)`
              : theme.primaryColor,
          }}
        >
          <h3 className="font-semibold text-white text-sm">{formName}</h3>
          <p className="text-xs text-white/80">Vyplňte formulář</p>
        </div>
      )}
      
      <div style={{ padding: paddingMap[theme.padding] }}>
        <div className="space-y-3">
          {(config.fields || []).filter((f: any) => !f.honeypot && !f.hidden).slice(0, 3).map((field) => (
            <div key={field.name}>
              {config.showLabels && (
                <label 
                  className="block text-xs font-medium mb-1"
                  style={{ color: theme.labelColor }}
                >
                  {field.label}
                  {field.required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
              )}
              <input
                type="text"
                disabled
                placeholder={config.showPlaceholders ? field.placeholder : ''}
                className="w-full text-xs py-1.5 px-2"
                style={{ 
                  borderRadius: borderRadiusMap[theme.borderRadius === 'full' ? 'lg' : theme.borderRadius],
                  border: `1px solid ${theme.borderColor}`,
                  backgroundColor: theme.backgroundColor,
                  color: theme.textColor,
                }}
              />
            </div>
          ))}
          {config.fields.length > 3 && (
            <p className="text-xs text-gray-400 text-center">
              +{config.fields.length - 3} dalších polí
            </p>
          )}
        </div>
        
        <button
          disabled
          className="mt-4 py-2 px-4 text-xs font-medium transition-colors"
          style={{ 
            width: theme.buttonFullWidth ? '100%' : 'auto',
            borderRadius: borderRadiusMap[theme.borderRadius === 'full' ? 'lg' : theme.borderRadius],
            backgroundColor: theme.buttonStyle === 'outline' ? 'transparent' : theme.primaryColor,
            color: theme.buttonStyle === 'outline' ? theme.primaryColor : '#ffffff',
            border: theme.buttonStyle === 'outline' ? `2px solid ${theme.primaryColor}` : 'none',
            background: theme.buttonStyle === 'gradient' 
              ? `linear-gradient(135deg, ${theme.primaryColor}, ${theme.primaryColor}cc)`
              : undefined,
          }}
        >
          {config.submitButtonText || 'Odeslat'}
        </button>
      </div>
    </div>
  );
}
